import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { cmsImportsApiClient } from '@/services/api/cmsImportsService';
import type {
  CmsImportCaseSchemaWithDetails,
  CmsImportTaskSchemaWithDetails,
} from '@/services/api/generated/cmsImportsService';

interface CMSImportCompletedPhaseProps {
  importId: string;
  cmsTypeLabel: string;
}

type TabType = 'imported' | 'skipped' | 'unmatched';

export const CMSImportCompletedPhase: React.FC<
  CMSImportCompletedPhaseProps
> = ({ importId, cmsTypeLabel }) => {
  const [activeTab, setActiveTab] = useState<TabType>('imported');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Delete mutation
  const [deleteCreatedTasks, { isLoading: isDeleting }] =
    cmsImportsApiClient.useCmsImportDeleteCreatedTasksMutation();

  // Fetch tasks and cases
  const { data: tasksData, isLoading: tasksLoading } =
    cmsImportsApiClient.useCmsImportTasksQuery({ id: importId });

  const { data: casesData, isLoading: casesLoading } =
    cmsImportsApiClient.useCmsImportCasesQuery({ id: importId });

  const tasks: CmsImportTaskSchemaWithDetails[] = useMemo(
    () => (Array.isArray(tasksData) ? tasksData : tasksData ? [tasksData] : []),
    [tasksData],
  );

  const cases: CmsImportCaseSchemaWithDetails[] = useMemo(
    () => (Array.isArray(casesData) ? casesData : casesData ? [casesData] : []),
    [casesData],
  );

  // Build case map for enrichment
  const caseMap = useMemo(() => {
    const map = new Map<string, CmsImportCaseSchemaWithDetails>();
    cases.forEach((c) => {
      map.set(c.cms_case.internal_id, c);
    });
    return map;
  }, [cases]);

  // Categorize tasks
  const { importedTasks, skippedTasks, createdTasksCount } = useMemo(() => {
    const imported: (CmsImportTaskSchemaWithDetails & {
      matterName?: string | null;
    })[] = [];
    const skipped: (CmsImportTaskSchemaWithDetails & {
      matterName?: string | null;
    })[] = [];
    let createdCount = 0;

    tasks.forEach((task) => {
      const caseMatch = task.cms_task.internal_case_id
        ? caseMap.get(task.cms_task.internal_case_id)
        : undefined;
      const enrichedTask = { ...task, matterName: caseMatch?.matter_name };

      if (task.import_action) {
        imported.push(enrichedTask);
        // Only count created tasks that still exist (have external_task_id)
        if (task.import_action === 'created' && task.external_task_id) {
          createdCount += 1;
        }
      } else {
        skipped.push(enrichedTask);
      }
    });

    // Sort by case name
    const sortByCaseName = (
      a: CmsImportTaskSchemaWithDetails,
      b: CmsImportTaskSchemaWithDetails,
    ) => {
      const nameA = a.cms_task.case_name?.toLowerCase() || '';
      const nameB = b.cms_task.case_name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    };

    return {
      importedTasks: imported.sort(sortByCaseName),
      skippedTasks: skipped.sort(sortByCaseName),
      createdTasksCount: createdCount,
    };
  }, [tasks, caseMap]);

  // Handle delete created tasks
  const handleDeleteCreatedTasks = async () => {
    try {
      const result = await deleteCreatedTasks({ id: importId }).unwrap();
      setIsDeleteDialogOpen(false);
      toast.success(
        `Successfully deleted ${result.deleted_count} created tasks`,
      );
    } catch (error) {
      toast.error('Failed to delete created tasks. Please try again.');
      console.error('Delete created tasks error:', error);
    }
  };

  // Get unmatched cases that had tasks we tried to import (skipped tasks)
  // Also compute the actual task count from skipped tasks
  const { unmatchedCases, caseTaskCounts } = useMemo(() => {
    // Count tasks per case from skipped tasks
    const taskCountMap = new Map<string, number>();
    skippedTasks.forEach((t) => {
      const caseId = t.cms_task.internal_case_id;
      if (caseId) {
        taskCountMap.set(caseId, (taskCountMap.get(caseId) || 0) + 1);
      }
    });

    // Only show unmatched cases that had tasks in the import
    const filteredCases = cases
      .filter((c) => !c.matter_id && taskCountMap.has(c.cms_case.internal_id))
      .sort((a, b) => {
        const nameA = a.cms_case.name?.toLowerCase() || '';
        const nameB = b.cms_case.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });

    return { unmatchedCases: filteredCases, caseTaskCounts: taskCountMap };
  }, [cases, skippedTasks]);

  // CSV Export functions
  const downloadCSV = (
    filename: string,
    headers: string[],
    rows: string[][],
  ) => {
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row.map((cell) => `"${(cell || '').replace(/"/g, '""')}"`).join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportImportedTasks = () => {
    const headers = [
      'CMS Case',
      'Matter',
      'Task Title',
      'Assignee',
      'Due Date',
      'Status',
      'Action',
    ];
    const rows = importedTasks.map((task) => [
      task.cms_task.case_name || '',
      task.matterName || '',
      task.cms_task.title || '',
      task.cms_task.assignee_name || '',
      task.cms_task.due_date || '',
      task.cms_task.status || '',
      task.import_action || '',
    ]);
    downloadCSV('imported-tasks.csv', headers, rows);
  };

  const exportSkippedTasks = () => {
    const headers = [
      'CMS Case',
      'Task Title',
      'Assignee',
      'Due Date',
      'Status',
      'Reason',
    ];
    const rows = skippedTasks.map((task) => [
      task.cms_task.case_name || '',
      task.cms_task.title || '',
      task.cms_task.assignee_name || '',
      task.cms_task.due_date || '',
      task.cms_task.status || '',
      'No matter match',
    ]);
    downloadCSV('skipped-tasks.csv', headers, rows);
  };

  const exportUnmatchedCases = () => {
    const headers = ['CMS Case Name', 'Skipped Tasks', 'Status'];
    const rows = unmatchedCases.map((c) => [
      c.cms_case.name || '',
      String(caseTaskCounts.get(c.cms_case.internal_id) || 0),
      c.cms_case.status || '',
    ]);
    downloadCSV('potentially-missing-matters.csv', headers, rows);
  };

  const isLoading = tasksLoading || casesLoading;

  const tabs = [
    {
      id: 'imported' as const,
      label: 'Imported Tasks',
      count: importedTasks.length,
      icon: CheckCircleIcon,
      iconColor: 'text-green-500',
    },
    {
      id: 'skipped' as const,
      label: 'Skipped Tasks',
      count: skippedTasks.length,
      icon: XCircleIcon,
      iconColor: 'text-zinc-400',
    },
    {
      id: 'unmatched' as const,
      label: 'Potentially Missing Matters',
      count: unmatchedCases.length,
      icon: ExclamationTriangleIcon,
      iconColor: 'text-amber-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-200 dark:border-zinc-800 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                Import Complete!
              </h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Your {cmsTypeLabel} tasks have been successfully processed.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/external-tasks"
              className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline text-sm"
            >
              View External Tasks
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Delete Created Tasks Section */}
      {createdTasksCount > 0 && (
        <div className="bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-900 dark:to-zinc-800/50 rounded-xl border border-zinc-200 dark:border-zinc-700/50 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 dark:bg-red-950/50 ring-1 ring-red-100 dark:ring-red-900/50">
                <TrashIcon className="h-5 w-5 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-white">
                  Need to undo this import?
                </h4>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                  {createdTasksCount}{' '}
                  {createdTasksCount === 1 ? 'task was' : 'tasks were'} created
                  during this import. You can permanently delete{' '}
                  {createdTasksCount === 1 ? 'it' : 'them'} if needed.
                </p>
              </div>
            </div>
            <Button
              color="red"
              onClick={() => setIsDeleteDialogOpen(true)}
              className="shrink-0 shadow-sm"
            >
              <TrashIcon className="h-4 w-4" />
              Delete Created Tasks
            </Button>
          </div>
        </div>
      )}

      {/* Tabs and Table */}
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-gray-200 dark:border-zinc-800">
        {/* Tab Bar */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-zinc-700 px-4">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                }`}
              >
                <tab.icon className={`h-4 w-4 ${tab.iconColor}`} />
                {tab.label}
                <span
                  className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Export Button */}
          <Button
            plain
            onClick={() => {
              if (activeTab === 'imported') exportImportedTasks();
              else if (activeTab === 'skipped') exportSkippedTasks();
              else exportUnmatchedCases();
            }}
            className="flex items-center gap-1.5 text-sm"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {/* Table Content */}
        <div className="overflow-auto max-h-[500px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-200 border-t-blue-600" />
            </div>
          ) : (
            <>
              {/* Imported Tasks Table */}
              {activeTab === 'imported' && (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 dark:bg-zinc-800 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        CMS Case
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Matter
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Task Title
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Assignee
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Due Date
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-zinc-700">
                    {importedTasks.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-zinc-500"
                        >
                          No tasks were imported
                        </td>
                      </tr>
                    ) : (
                      importedTasks.map((task) => (
                        <tr
                          key={task.id}
                          className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                        >
                          <td className="px-4 py-3 text-zinc-900 dark:text-white">
                            {task.cms_task.case_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {task.matterName || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-900 dark:text-white">
                            {task.cms_task.title || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {task.cms_task.assignee_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {task.cms_task.due_date || '-'}
                          </td>
                          <td className="px-4 py-3">
                            {task.import_action === 'created' &&
                            !task.external_task_id ? (
                              <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                                Deleted
                              </span>
                            ) : (
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  task.import_action === 'created'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                }`}
                              >
                                {task.import_action === 'created'
                                  ? 'Created'
                                  : 'Updated'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {/* Skipped Tasks Table */}
              {activeTab === 'skipped' && (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 dark:bg-zinc-800 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        CMS Case
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Task Title
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Assignee
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Due Date
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-zinc-700">
                    {skippedTasks.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-8 text-center text-zinc-500"
                        >
                          No tasks were skipped
                        </td>
                      </tr>
                    ) : (
                      skippedTasks.map((task) => (
                        <tr
                          key={task.id}
                          className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                        >
                          <td className="px-4 py-3 text-zinc-900 dark:text-white">
                            {task.cms_task.case_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-900 dark:text-white">
                            {task.cms_task.title || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {task.cms_task.assignee_name || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {task.cms_task.due_date || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {task.cms_task.status || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                              No matter match
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {/* Unmatched Cases Table */}
              {activeTab === 'unmatched' && (
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 dark:bg-zinc-800 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        CMS Case Name
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Skipped Tasks
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                        Recommendation
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-zinc-700">
                    {unmatchedCases.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-8 text-center text-zinc-500"
                        >
                          All cases matched to matters
                        </td>
                      </tr>
                    ) : (
                      unmatchedCases.map((caseItem) => (
                        <tr
                          key={caseItem.id}
                          className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                        >
                          <td className="px-4 py-3 text-zinc-900 dark:text-white">
                            {caseItem.cms_case.name || '-'}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {caseTaskCounts.get(
                              caseItem.cms_case.internal_id,
                            ) || 0}
                          </td>
                          <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">
                            {caseItem.cms_case.status || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">
                              Create a new matter or link to existing
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        size="md"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/50">
            <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <DialogTitle>Delete Created Tasks</DialogTitle>
        </div>
        <DialogDescription>
          Are you sure you want to delete all {createdTasksCount}{' '}
          {createdTasksCount === 1 ? 'task' : 'tasks'} that{' '}
          {createdTasksCount === 1 ? 'was' : 'were'} created during this import?
        </DialogDescription>
        <DialogBody>
          <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 p-4">
            <div className="flex gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  This action cannot be undone
                </p>
                <p className="mt-1 text-amber-700 dark:text-amber-300/80">
                  The external tasks will be permanently removed from the
                  system. Updated tasks will not be affected.
                </p>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setIsDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDeleteCreatedTasks}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Deleting...
              </>
            ) : (
              <>
                <TrashIcon className="h-4 w-4" />
                Delete {createdTasksCount}{' '}
                {createdTasksCount === 1 ? 'Task' : 'Tasks'}
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
